import { useMemo } from 'react';
import { pipe } from 'fp-ts/function';
import { PortfolioCentralPaymentPolicyDetail, PortfolioPaymentPolicyTrigger } from '@mews-commander/data-access';
import { DurationService, getDefaults } from '@mews/framework';
import { SettlementRuleOffsetType } from '../../../../../settlementRules/types';
import { FormValues, validationSchema } from '../schema';
import { CentralPaymentPolicyFormField } from '../types';

export const useGetInitialFormValues = (data?: PortfolioCentralPaymentPolicyDetail | null) =>
    useMemo(() => {
        const defaultValues = getDefaults(validationSchema) as FormValues;
        const { name, key, trigger, action, type } = data || defaultValues;

        const offsetDuration = data?.offset
            ? pipe(data.offset, DurationService.parse)
            : pipe(defaultValues.offset, DurationService.fromObject);

        const formValues = {
            [CentralPaymentPolicyFormField.Name]: name ?? '',
            [CentralPaymentPolicyFormField.PaymentPolicyKey]: key ?? '',
            [CentralPaymentPolicyFormField.Trigger]: trigger,
            [CentralPaymentPolicyFormField.Amount]: data
                ? ({
                      type: data.value.discriminator === 'Percentage' ? 'percentage' : 'flat',
                      value: data.value.value.value,
                  } as unknown as FormValues['amount'])
                : { type: 'flat', value: 0 },
            [CentralPaymentPolicyFormField.Offset]: pipe(offsetDuration, DurationService.absolute, (duration) =>
                DurationService.toObject(duration, { partial: false })
            ),
            [CentralPaymentPolicyFormField.OffsetType]:
                DurationService.isNegative(offsetDuration) ||
                (DurationService.isZero(offsetDuration) && trigger !== PortfolioPaymentPolicyTrigger.Confirmation)
                    ? SettlementRuleOffsetType.Before
                    : SettlementRuleOffsetType.After,
            [CentralPaymentPolicyFormField.Action]: action,
            [CentralPaymentPolicyFormField.Type]: type,
        } satisfies FormValues;
        return {
            defaultValues,
            formValues,
        };
    }, [data]);
